import { Box, Flex } from "@chakra-ui/react";
import { format } from "date-fns";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Navigate } from "react-router-dom";
import type { OptionTypeBase, OptionsType } from "react-select";

import { SolidButton } from "~/src/components/common/Button";
import {
  MultipleSelect,
  type MultipleSelectProps,
} from "~/src/components/common/Select";
import { AdminReportResults } from "~/src/components/features/adminReport/AdminReportResults";
import { DateRangeContext } from "~/src/components/features/global/HeaderNavigation/DateRangeField";
import { PageLayout } from "~/src/components/features/global/PageLayout";
import { useAdminReportSiteCache } from "~/src/lib/hooks/useAdminReportSiteCache";
import { useUpdateDebounce } from "~/src/lib/react-use/useUpdateDebounce";

// NOTE: viewer 用クエリ（サイトID取得 + ロール判定）
const ADMIN_REPORT_SITE_CACHE_KEY = "admin-report";

const viewerQuery = graphql`
  query AdminReportPageContainer_ViewerQuery($searchTerm: String) {
    viewer {
      role
      sites(first: 500, searchTerm: $searchTerm) {
        nodes {
          id
          name
        }
      }
    }
  }
`;

// NOTE: レポートクエリは AdminReportResults 側に定義

type SortState = {
  field: string; // maps to SiteReportOrderField-like keys
  direction: "ASC" | "DESC";
};

type SiteOption = {
  label: string;
  value: string;
};

export const AdminReportPageContainer: FC = () => {
  const {
    dateRange: [startOn, endOn],
  } = useContext(DateRangeContext);

  // Mirror date range from context into local state to drive commits on change
  const [dateRange, setDateRange] = useState<[Date, Date]>([startOn, endOn]);

  const [sort, setSort] = useState<SortState>({
    field: "PAGE_VIEW_USER",
    direction: "DESC",
  });

  const [committedSiteIds, setCommittedSiteIds] = useState<string[] | null>(
    null
  );

  const orderValueMap: Record<string, string> = {
    UNIQUE_USER: "UNIQUE_USER",
    POPUP_READY_USER: "READY_USER",
    PAGE_VIEW_USER: "PAGE_VIEW_USER",
    CLOSE_POPUP: "CLOSE_CLICK",
    CLICK_POPUP: "CLICK_USER",
    POPUP_CTR: "CLICK_USER_RATE",
    POPUP_CONVERSION: "TOTAL_CONVERSION_USER",
    POPUP_CVR: "TOTAL_CONVERSION_RATE",
  };

  // 1. viewer クエリ実行（ロールとサイトID取得）
  const [siteSearch, setSiteSearch] = useState("");
  const [debouncedSiteSearch, setDebouncedSiteSearch] = useState("");

  useUpdateDebounce(() => setDebouncedSiteSearch(siteSearch), 300, [
    siteSearch,
  ]);

  const { selectedSites, setSelectedSites } = useAdminReportSiteCache({
    cacheKey: ADMIN_REPORT_SITE_CACHE_KEY,
  });

  const viewerData: any = useLazyLoadQuery(
    viewerQuery,
    { searchTerm: debouncedSiteSearch || null },
    {
      fetchPolicy: "store-and-network",
    }
  );

  // 2. admin レポートクエリ用の変数
  const siteOptions = useMemo<SiteOption[]>(() => {
    return (viewerData.viewer.sites.nodes || [])
      .filter(Boolean)
      .map((n: { id: string; name: string }) => ({
        label: n.name,
        value: n.id,
      }));
  }, [viewerData]);

  const handleSortClick = useCallback((params: SortState) => {
    setSort({ field: params.field, direction: params.direction });
  }, []);

  const handleSiteChange: NonNullable<MultipleSelectProps["onChange"]> =
    useCallback(
      (value) => {
        const opts = Array.isArray(value)
          ? (value as OptionsType<OptionTypeBase>)
          : [];
        setSelectedSites(
          opts
            .map((o) => {
              const option = o as { value?: string; label?: string };
              if (typeof option.value !== "string") return null;
              return { id: option.value, label: option.label ?? option.value };
            })
            .filter((v): v is { id: string; label: string } => Boolean(v))
        );
        setSiteSearch("");
      },
      [setSelectedSites]
    );

  const handleDisplayClick = useCallback(() => {
    setCommittedSiteIds(selectedSites.map((s) => s.id));
  }, [selectedSites]);

  const handleSiteInputChange = useCallback(
    (value: string, meta: { action: string }) => {
      if (meta.action === "input-change") setSiteSearch(value);
    },
    []
  );

  // Commit current selections when the date range changes via the global header
  useEffect(() => {
    const [prevStart, prevEnd] = dateRange;
    const changed =
      prevStart.getTime() !== startOn.getTime() ||
      prevEnd.getTime() !== endOn.getTime();
    if (changed) {
      setDateRange([startOn, endOn]);
      setCommittedSiteIds(selectedSites.map((s) => s.id));
    }
  }, [startOn, endOn, selectedSites, dateRange]);

  // Gate: ADMIN or SUPER users only (すべてのフック呼び出し後にガード)
  const allowedRoles = ["ADMIN", "SUPER"];

  if (!allowedRoles.includes(viewerData.viewer.role))
    return <Navigate to="/" />;

  return (
    <PageLayout title="管理者用ポップアップレポート(案件横断のポップアップレポート)">
      <Flex justifyContent="flex-end" mb="16px" mt={4} gap="12px">
        <Box flex="1" maxW="100%">
          <MultipleSelect
            options={siteOptions}
            value={selectedSites.map((s) => ({ value: s.id, label: s.label }))}
            placeholder="サイトを選択"
            onChange={handleSiteChange}
            isSearchable
            onInputChange={handleSiteInputChange}
            styles={{
              container: (base: any) => ({ ...base, width: "100%" }),
              control: (base: any) => ({ ...base, width: "100%" }),
              menu: (base: any) => ({ ...base, zIndex: 10, width: "100%" }),
            }}
          />
        </Box>
        <SolidButton
          onClick={handleDisplayClick}
          isDisabled={!selectedSites.length}
        >
          表示
        </SolidButton>
      </Flex>
      {committedSiteIds && committedSiteIds.length > 0 ? (
        <AdminReportResults
          startDate={format(dateRange[0], "yyyy-MM-dd")}
          endDate={format(dateRange[1], "yyyy-MM-dd")}
          siteIds={committedSiteIds}
          orderValue={orderValueMap[sort.field] || "PAGE_VIEW_USER"}
          isDesc={sort.direction === "DESC"}
          onSortClick={handleSortClick}
        />
      ) : (
        <div>サイトを選択して「表示」を押してください。</div>
      )}
    </PageLayout>
  );
};

// NOTE: 表示部は features/adminReport/AdminReportResults.tsx に切り出し
